/**
 * Modal per creare/gestire una richiesta di bozza grafica per una riga di preventivo.
 * Utilizzata all'interno di AgentQuoteNew.js per ciascuna riga.
 */
import React, { useEffect, useState } from 'react';
import { X, Upload, Palette, Loader2, Trash2, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GraphicRequestModal = ({ quoteId, itemIndex, defaultProductName, existingRequestId, agentToken, onClose, onSaved }) => {
  const [request, setRequest] = useState(null);
  const [productName, setProductName] = useState(defaultProductName || '');
  const [colors, setColors] = useState([]);
  const [colorInput, setColorInput] = useState('');
  const [notes, setNotes] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!!existingRequestId);

  const auth = () => ({ Authorization: `Bearer ${agentToken}` });

  useEffect(() => {
    if (existingRequestId) {
      (async () => {
        try {
          const r = await fetch(`${API}/api/agent/graphic-requests/${existingRequestId}`, { headers: auth() });
          if (r.ok) {
            const j = await r.json();
            const d = j.data;
            setRequest(d);
            setProductName(d.product_name || '');
            setColors(d.colors || []);
            setNotes(d.notes || '');
          }
        } finally { setLoading(false); }
      })();
    }
    // eslint-disable-next-line
  }, [existingRequestId]);

  const addColor = () => {
    const v = colorInput.trim();
    if (!v) return;
    if (colors.includes(v)) return;
    setColors([...colors, v]);
    setColorInput('');
  };

  const removeColor = (c) => setColors(colors.filter((x) => x !== c));

  const submit = async () => {
    if (!productName.trim()) { setError('Nome prodotto obbligatorio'); return; }
    if (!quoteId) { setError('Salva prima il preventivo (Crea preventivo) e poi torna a richiedere la grafica.'); return; }
    setSaving(true); setError('');
    try {
      let reqId = request?.id;
      if (!reqId) {
        // create
        const r = await fetch(`${API}/api/agent/graphic-requests`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...auth() },
          body: JSON.stringify({
            quote_id: quoteId,
            quote_item_index: itemIndex,
            product_name: productName.trim(),
            colors,
            notes: notes || null,
          }),
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || 'Errore creazione');
        reqId = j.data.id;
        setRequest(j.data);
      } else {
        // update
        const r = await fetch(`${API}/api/agent/graphic-requests/${reqId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...auth() },
          body: JSON.stringify({ product_name: productName.trim(), colors, notes: notes || null }),
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.detail || 'Errore update');
        setRequest(j.data);
      }

      // Upload logo se selezionato
      if (logoFile) {
        const form = new FormData();
        form.append('file', logoFile);
        const r = await fetch(`${API}/api/agent/graphic-requests/${reqId}/logo`, {
          method: 'POST',
          headers: auth(),
          body: form,
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || 'Errore upload logo');
        }
        const j = await r.json();
        setRequest(j.data);
      }

      onSaved && onSaved(reqId);
      onClose();
    } catch (e) {
      setError(e.message || 'Errore imprevisto');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento richiesta…
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="graphic-request-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-fuchsia-500 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Palette className="w-5 h-5" /> Richiesta bozza grafica
            </h2>
            <div className="text-[11px] text-white/80">
              Riga #{itemIndex + 1} del preventivo
              {request && <span className="ml-2 font-mono">· {request.id}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4 text-sm">
          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Nome prodotto *</label>
            <input value={productName} onChange={(e) => setProductName(e.target.value)}
              data-testid="graphic-product-name"
              placeholder="Es. Amaro Barolo Chinato — Edizione Nozze Rossi"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <div className="text-[10px] text-gray-500 mt-0.5">Comparirà sull&apos;etichetta.</div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Colori (opzionale)</label>
            <div className="mt-1 flex gap-2">
              <input value={colorInput} onChange={(e) => setColorInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addColor(); } }}
                data-testid="graphic-color-input"
                placeholder="Es. #C89B3C, oro antico, bordeaux…"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm" />
              <button type="button" onClick={addColor} className="px-3 py-2 bg-purple-100 text-purple-700 rounded-md text-sm hover:bg-purple-200">
                Aggiungi
              </button>
            </div>
            {colors.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {colors.map((c) => {
                  const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(c);
                  return (
                    <span key={c} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-xs">
                      {isHex && <span className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: c }} />}
                      {c}
                      <button type="button" onClick={() => removeColor(c)} className="text-red-500 hover:text-red-700 ml-0.5">×</button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Logo cliente (opzionale)</label>
            <div className="mt-1 flex items-center gap-2">
              <label className="flex-1 cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-dashed border-purple-300 bg-purple-50/40 rounded-md text-sm text-purple-700 hover:bg-purple-50">
                <Upload className="w-4 h-4" />
                {logoFile ? logoFile.name : (request?.logo_original_name || 'Carica logo (PNG/JPG/SVG/PDF, max 5MB)')}
                <input type="file" className="hidden"
                  data-testid="graphic-logo-input"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml,application/pdf"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              </label>
              {request?.logo_url && (
                <a href={request.logo_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-1 border rounded text-xs text-purple-700 hover:bg-purple-50">
                  <ImageIcon className="w-3 h-3" /> Vedi
                </a>
              )}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Note per il grafico (opzionale)</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="Es. stile art deco, font serif elegante, evitare rosso, immagine centrale di un&apos;ancora…"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
          </div>

          {request && (
            <div className="bg-purple-50 border border-purple-200 rounded-md p-3 text-xs">
              <div className="font-semibold text-purple-900 mb-1">Stato attuale:</div>
              <div className="text-purple-800 uppercase font-mono">{request.status}</div>
              {(request.design_files || []).length > 0 && (
                <div className="text-purple-700 mt-1">📎 {request.design_files.length} file caricati dall'admin</div>
              )}
            </div>
          )}

          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm">Annulla</button>
          <button type="button" onClick={submit} disabled={saving} data-testid="btn-save-graphic-request"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {request ? 'Aggiorna richiesta' : 'Crea richiesta grafica'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GraphicRequestModal;
